/**
 * ══════════════════════════════════════════════════════════════
 * NETUNO — Engine de Regras para Insights Analíticos
 * ══════════════════════════════════════════════════════════════
 *
 * Regras simples que analisam dados dos dashboards e geram
 * insights acionáveis. Preparado para expansão futura.
 *
 * Uso: import { generateInsights } from '@/lib/insightRules';
 * ══════════════════════════════════════════════════════════════
 */

import type {
  AnalyticalInsight,
  InsightRule,
  InsightRuleContext,
} from '@/types/insights';

// ─── Rules ──────────────────────────────────────────────────

const RULES: InsightRule[] = [
  // R1: Concentração de risco crítico na carteira
  {
    code: 'RISK_CONCENTRATION',
    name: 'Concentração de Risco',
    active: true,
    defaultSeverity: 'critical',
    category: 'risk_concentration',
    evaluate: (ctx) => {
      const insights: AnalyticalInsight[] = [];
      if (!ctx.portfolio || ctx.portfolio.total === 0) return insights;

      const pctCriticos = ctx.portfolio.criticos / ctx.portfolio.total;
      if (pctCriticos >= 0.3) {
        insights.push({
          id: 'risk-concentration-critical',
          title: `${Math.round(pctCriticos * 100)}% da carteira em nível crítico`,
          description: `${ctx.portfolio.criticos} de ${ctx.portfolio.total} contratos estão com score de criticidade acima de 80 pontos, indicando risco sistêmico na carteira.`,
          severity: 'critical',
          category: 'risk_concentration',
          relatedMetric: 'score_criticidade',
          recommendedAction: 'Revisar contratos críticos e priorizar planos de mitigação imediatos.',
          drillDownRoute: '/empreendimentos/inteligencia/uo',
          timestamp: new Date().toISOString(),
          ruleCode: 'RISK_CONCENTRATION',
        });
      } else if (pctCriticos >= 0.15) {
        insights.push({
          id: 'risk-concentration-warning',
          title: `${Math.round(pctCriticos * 100)}% da carteira em nível crítico`,
          description: `${ctx.portfolio.criticos} contratos estão com criticidade elevada. Monitorar de perto para evitar escalada.`,
          severity: 'warning',
          category: 'risk_concentration',
          relatedMetric: 'score_criticidade',
          recommendedAction: 'Acompanhar evolução semanal dos contratos críticos.',
          drillDownRoute: '/empreendimentos/risk-analytics',
          timestamp: new Date().toISOString(),
          ruleCode: 'RISK_CONCENTRATION',
        });
      }
      return insights;
    },
  },

  // R2: Deterioração generalizada
  {
    code: 'PORTFOLIO_DETERIORATION',
    name: 'Deterioração da Carteira',
    active: true,
    defaultSeverity: 'critical',
    category: 'trend_deterioration',
    evaluate: (ctx) => {
      const insights: AnalyticalInsight[] = [];
      if (!ctx.portfolio || ctx.portfolio.total === 0) return insights;

      const pctPiorando = ctx.portfolio.piorando / ctx.portfolio.total;
      if (pctPiorando >= 0.3) {
        insights.push({
          id: 'portfolio-deterioration',
          title: `${ctx.portfolio.piorando} contratos em deterioração`,
          description: `${Math.round(pctPiorando * 100)}% da carteira apresenta tendência de piora, acima do limiar de 30%. Possível causa sistêmica.`,
          severity: 'critical',
          category: 'trend_deterioration',
          relatedMetric: 'tendencia',
          recommendedAction: 'Investigar causas comuns entre contratos em deterioração via análise de tendências.',
          drillDownRoute: '/empreendimentos/inteligencia/tendencias',
          timestamp: new Date().toISOString(),
          ruleCode: 'PORTFOLIO_DETERIORATION',
        });
      }
      return insights;
    },
  },

  // R3: Contrato individual com score muito alto
  {
    code: 'HIGH_SCORE_CONTRACT',
    name: 'Contrato com Score Extremo',
    active: true,
    defaultSeverity: 'critical',
    category: 'threshold_breach',
    evaluate: (ctx) => {
      const insights: AnalyticalInsight[] = [];
      if (!ctx.contracts) return insights;

      const extreme = ctx.contracts.filter(c => c.score_criticidade >= 90);
      for (const c of extreme.slice(0, 3)) {
        insights.push({
          id: `extreme-score-${c.contract_id}`,
          title: `${c.contrato_nome}: score ${c.score_criticidade}`,
          description: `Contrato com score de criticidade extremo (≥90), indicando risco iminente de falha em prazo ou custo.`,
          severity: 'critical',
          category: 'threshold_breach',
          relatedMetric: 'score_criticidade',
          relatedEntity: { type: 'contract', id: c.contract_id, name: c.contrato_nome },
          recommendedAction: 'Acionar revisão executiva imediata e avaliar plano de contingência.',
          drillDownRoute: `/empreendimentos/inteligencia/${c.contract_id}`,
          timestamp: new Date().toISOString(),
          ruleCode: 'HIGH_SCORE_CONTRACT',
        });
      }
      return insights;
    },
  },

  // R4: Muitas ocorrências em um contrato
  {
    code: 'HIGH_OCCURRENCES',
    name: 'Alto Volume de Ocorrências',
    active: true,
    defaultSeverity: 'warning',
    category: 'operational',
    evaluate: (ctx) => {
      const insights: AnalyticalInsight[] = [];
      if (!ctx.contracts) return insights;

      const highOccurrence = ctx.contracts.filter(c => (c.total_ocorrencias ?? 0) >= 10);
      for (const c of highOccurrence.slice(0, 2)) {
        insights.push({
          id: `high-occurrences-${c.contract_id}`,
          title: `${c.contrato_nome}: ${c.total_ocorrencias} ocorrências`,
          description: `Contrato com alto volume de não-conformidades ou alertas ativos, indicando problemas operacionais recorrentes.`,
          severity: 'warning',
          category: 'operational',
          relatedMetric: 'total_ocorrencias',
          relatedEntity: { type: 'contract', id: c.contract_id, name: c.contrato_nome },
          recommendedAction: 'Avaliar padrão de recorrência e acionar equipe de qualidade.',
          drillDownRoute: `/empreendimentos/inteligencia/${c.contract_id}`,
          timestamp: new Date().toISOString(),
          ruleCode: 'HIGH_OCCURRENCES',
        });
      }
      return insights;
    },
  },

  // R5: UO com concentração de risco
  {
    code: 'UO_RISK_CONCENTRATION',
    name: 'UO com Concentração de Risco',
    active: true,
    defaultSeverity: 'warning',
    category: 'risk_concentration',
    evaluate: (ctx) => {
      const insights: AnalyticalInsight[] = [];
      if (!ctx.uos) return insights;

      const critical = ctx.uos.filter(u => u.nivel_risco === 'critico');
      if (critical.length >= 2) {
        insights.push({
          id: 'uo-multiple-critical',
          title: `${critical.length} UOs em nível crítico`,
          description: `Múltiplas unidades operacionais apresentam concentração de risco crítico: ${critical.map(u => u.uo_nome).join(', ')}.`,
          severity: 'critical',
          category: 'risk_concentration',
          relatedMetric: 'nivel_risco',
          recommendedAction: 'Priorizar intervenção nas UOs com maior número de contratos críticos.',
          drillDownRoute: '/empreendimentos/inteligencia/uo',
          timestamp: new Date().toISOString(),
          ruleCode: 'UO_RISK_CONCENTRATION',
        });
      }

      for (const uo of critical.slice(0, 2)) {
        insights.push({
          id: `uo-critical-${uo.uo_id}`,
          title: `${uo.uo_nome}: nível crítico`,
          description: `UO com ${uo.total_contratos} contratos e score médio de ${uo.score_medio?.toFixed(0) || 'N/A'} pontos.`,
          severity: 'warning',
          category: 'risk_concentration',
          relatedMetric: 'score_medio',
          relatedEntity: { type: 'uo', id: uo.uo_id, name: uo.uo_nome },
          recommendedAction: 'Analisar detalhamento por contrato nesta UO.',
          timestamp: new Date().toISOString(),
          ruleCode: 'UO_RISK_CONCENTRATION',
        });
      }
      return insights;
    },
  },

  // R6: Tendência de melhoria (insight positivo)
  {
    code: 'PORTFOLIO_IMPROVING',
    name: 'Carteira em Melhoria',
    active: true,
    defaultSeverity: 'info',
    category: 'trend_improvement',
    evaluate: (ctx) => {
      const insights: AnalyticalInsight[] = [];
      if (!ctx.portfolio || ctx.portfolio.total === 0) return insights;

      if (ctx.portfolio.piorando === 0 && ctx.portfolio.total > 0) {
        insights.push({
          id: 'portfolio-improving',
          title: 'Carteira sem contratos em deterioração',
          description: 'Nenhum contrato apresenta tendência de piora no momento. A carteira está estável ou em melhoria.',
          severity: 'info',
          category: 'trend_improvement',
          relatedMetric: 'tendencia',
          timestamp: new Date().toISOString(),
          ruleCode: 'PORTFOLIO_IMPROVING',
        });
      }
      return insights;
    },
  },

  // R7: Contratos em risco crescente (trends)
  {
    code: 'GROWING_RISK_TREND',
    name: 'Risco Crescente Detectado',
    active: true,
    defaultSeverity: 'warning',
    category: 'trend_deterioration',
    evaluate: (ctx) => {
      const insights: AnalyticalInsight[] = [];
      if (!ctx.trends) return insights;

      const worsening = ctx.trends.filter(t => t.direcao === 'piorando' && t.variacao >= 10);
      for (const t of worsening.slice(0, 3)) {
        insights.push({
          id: `growing-risk-${t.contract_id}`,
          title: `${t.contrato_nome}: +${t.variacao.toFixed(0)}pp`,
          description: `Contrato com aumento significativo (≥10pp) na probabilidade de atraso entre períodos consecutivos.`,
          severity: t.variacao >= 20 ? 'critical' : 'warning',
          category: 'trend_deterioration',
          relatedMetric: 'prob_atraso',
          relatedEntity: { type: 'contract', id: t.contract_id, name: t.contrato_nome },
          recommendedAction: 'Investigar causa raiz do aumento e reavaliar cronograma.',
          drillDownRoute: `/empreendimentos/inteligencia/${t.contract_id}`,
          timestamp: new Date().toISOString(),
          ruleCode: 'GROWING_RISK_TREND',
        });
      }
      return insights;
    },
  },
];

// ─── Engine ─────────────────────────────────────────────────

export function generateInsights(context: InsightRuleContext): AnalyticalInsight[] {
  const insights: AnalyticalInsight[] = [];

  for (const rule of RULES) {
    if (!rule.active) continue;
    try {
      const result = rule.evaluate(context);
      insights.push(...result);
    } catch (err) {
      console.warn(`[InsightEngine] Rule ${rule.code} failed:`, err);
    }
  }

  // Sort by severity then timestamp
  return insights.sort((a, b) => {
    const sev = { critical: 0, warning: 1, info: 2 };
    const diff = sev[a.severity] - sev[b.severity];
    if (diff !== 0) return diff;
    return new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();
  });
}

/** Get active rule count */
export function getActiveRuleCount(): number {
  return RULES.filter(r => r.active).length;
}

/** Get all rule codes */
export function getRuleCodes(): string[] {
  return RULES.map(r => r.code);
}
